import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { DailyReportData } from '@construction/modules/progress-tracking/types/daily-report.types';
import { PaginatedResponse } from '@core/models/project.model';
import { map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import {
  BulkEodsToSave,
  DroneOrthoListParamsType,
  DroneOrthoPcsClasses,
  EodByDate,
  EstimateOfDayScope,
  EstimateOfDayScopeCumulative,
  GetConstructionProjectsForInvitationQueryParams,
  GetConstructionProjectsQueryParams,
  OrthoAcBlock,
  OrthoDate,
  OrthoImage,
  ProjectPreset,
  ScopeLockInfo,
  UpdateAutoDetectionReviewedStatus,
  WbsConfigEdit,
  WbsConfiguration,
  WbsConfigurationScope,
  WbsOriginalPlanData,
} from '../../../@construction/construction.types';
import {
  ConstructionProjectCreateModel,
  ConstructionProjectList,
  ConstructionProjectLoadModel,
  ConstructionProjectProductionDays,
  ConstructionProjectSaveModel,
  EmptyPackReturnsResponse,
  GetConstructionProjectOptions,
  NonWorkingDays,
  ProjectWorkhours,
  Sensor,
} from '../../../@construction/models/construction-project.model';
import { TBResponse } from '@construction/models/wbs-elements.model';
import {
  AddMilestoneModel,
  IMilestone,
} from '@construction/modules/project-edit/modules/construction-milestones/milestones.model';
import {
  RoverForm,
  Rover,
} from '@construction/modules/project-edit/modules/construction-devices/components/rovers-list/rovers-list.component';
import { SupplyPlanResponseData } from '@construction/modules/project-edit/modules/logistics/views/supply-plan/supply-plan.type';
import { EquipmentSpecs } from '@construction/modules/project-edit/projects.types';
import { EmptyPackReturnsResponseData } from '@construction/modules/project-edit/modules/logistics/views/empty-pack-returns/empty-pack-returns.type';
import { ProjectUserContactProfile } from '@core/interfaces/user.interface';
import { ContactProfileModel } from '@user/user.component';
import { EPSGProjectionDataType } from '@construction/modules/project-edit/modules/construction-gcp/construction-gcp.types';
import { EodsByUserType } from '@construction/modules/progress-tracking/types/data-entry.types';
import { PanoramicImage } from '@construction/shared/view-pano-image-dialog/view-pano-image-dialog.component';

export type OrthoParams = {
  page_size: number;
  page: number;
  flight_start_from?: string;
  flight_start_to?: string;
  drone_orthos_id?: string;
  status?: string;
};

export type PanoParams = {
  page_size: number;
  page: number;
  date_from?: string;
  date_to?: string;
};

@Injectable({ providedIn: 'root' })
export class ConstructionProjectApiService {
  private http = inject(HttpClient);

  public createConstructionProject(data: ConstructionProjectCreateModel | FormData) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/`;

    return this.http.post<TBResponse<ConstructionProjectLoadModel>>(url, data).pipe(map((response) => response.data));
  }

  public getConstructionProjects(queryParams: GetConstructionProjectsQueryParams) {
    const paramsObj = {
      ...queryParams,
      is_archived:
        queryParams.is_archived === null
          ? undefined
          : queryParams.is_archived === undefined
            ? false
            : queryParams.is_archived,
    };

    const cleanedParams: Record<string, string | number | boolean> = Object.fromEntries(
      Object.entries(paramsObj).filter(([, v]) => v !== undefined),
    ) as Record<string, string | number | boolean>;

    const params = new HttpParams({ fromObject: cleanedParams });
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/`;

    return this.http.get<PaginatedResponse<ConstructionProjectList>>(url, { params });
  }

  public getConstructionProjectsForInvitation(
    companyId: string,
    queryParams: GetConstructionProjectsForInvitationQueryParams,
  ) {
    const params = new HttpParams({ fromObject: queryParams });
    const url = `${environment.apiUrl}/${environment.apiVersion}/companies/${companyId}/construction_projects_for_invitations/`;

    return this.http.get<PaginatedResponse<ConstructionProjectList>>(url, { params });
  }

  public getConstructionProject(projectId: string, options?: GetConstructionProjectOptions) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${projectId}/`;

    return this.http
      .get<TBResponse<ConstructionProjectLoadModel>>(url, {
        params: {
          with_wbs_config: options ? options.withWbsConfig : true,
          with_daily_digest_contact_profiles: options ? options.withDailyDigestContactProfiles : true,
          with_procore_integration: options ? options.withProcoreIntegration : true,
          with_project_workhours: options ? options.withProjectWorkhours : true,
        },
      })
      .pipe(map((response) => response.data));
  }

  public updateConstructionProject(projectId: string, data: ConstructionProjectSaveModel | undefined) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${projectId}/`;

    return this.http.patch(url, data);
  }

  public updateConstructionProjectToArchived(projectId: string, toArchive: boolean) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${projectId}/archive/`;

    return this.http.patch(url, { to_archive: toArchive });
  }

  public updateNonWorkingDays(
    projectId: string,
    nonWorkingDays?: NonWorkingDays,
    projectWorkHours?: ProjectWorkhours,
    productionDates?: ConstructionProjectProductionDays,
  ) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${projectId}/non_working_days/`;

    return this.http.patch(url, {
      non_working_days: nonWorkingDays,
      project_workhours: projectWorkHours,
      drone_production_dates: productionDates,
    });
  }

  public submitConstructionProject(projectId: string) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${projectId}/submit/`;

    return this.http.post(url, {});
  }

  public verifyConstructionProject(projectId: string, geoProjectUuid: string) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${projectId}/verify/${geoProjectUuid}/`;

    return this.http.post(url, {});
  }

  public addConstructionProjectDocument(projectId: string, data: FormData) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${projectId}/documents/`;

    return this.http.post<TBResponse<ConstructionProjectLoadModel>>(url, data);
  }

  public deleteConstructionProjectDocument(projectId: string, fileId: string) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${projectId}/documents/${fileId}/`;

    return this.http.delete<TBResponse<ConstructionProjectLoadModel>>(url);
  }

  public getDroneOrthos(
    construction_project_pk: string,
    querryParams: OrthoParams,
    autoDetection = false,
    params: HttpParams,
  ) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${construction_project_pk}/drone_orthos/${
      querryParams.drone_orthos_id ? querryParams.drone_orthos_id + '/' : ''
    }?auto_detect=${autoDetection}`;

    return this.http.get<PaginatedResponse<OrthoImage>>(url, {
      params: params,
    });
  }

  public getDroneOrtho(construction_project_pk: string, drone_orthos_id?: string) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${construction_project_pk}/drone_orthos/${drone_orthos_id + '/'}`;

    return this.http.get<TBResponse<OrthoImage>>(url).pipe(map((response) => response.data));
  }

  public uploadDroneOrthos(projectId: string, data?: OrthoImage) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${projectId}/drone_orthos/`;

    return this.http.post<TBResponse<ConstructionProjectLoadModel>>(url, data).pipe(map((response) => response.data));
  }

  public deleteDroneOrtho(projectId: string, droneFlightId: string) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${projectId}/drone_orthos/${droneFlightId}/`;

    return this.http.delete(url);
  }

  public updateDroneOrthos(projectId: string, droneFlightId: string, data?: any) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${projectId}/drone_orthos/${droneFlightId}/`;

    return this.http.patch<TBResponse<OrthoImage>>(url, data).pipe(map((response) => response.data));
  }

  public publishElementDetectionOrtho(projectId: string, orthoId: string) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${projectId}/drone_orthos/${orthoId}/publish/`;

    return this.http.patch<TBResponse<OrthoImage>>(url, {}).pipe(map((response) => response.data));
  }

  public batchTransformElementDetectionOrtho(projectId: string, orthoId: string) {
    return this.http.put(
      `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${projectId}/drone_orthos/${orthoId}/ml_batch_transform/`,
      {},
    );
  }

  public triggerElementDetection(projectId: string, orthoId: string) {
    return this.http.put(
      `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${projectId}/drone_orthos/${orthoId}/drone_ortho_ml_detection/`,
      {},
    );
  }

  public getOrthoListDates(projectId: string, droneOrthoParams: DroneOrthoListParamsType) {
    const params = new HttpParams({ fromObject: droneOrthoParams });
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${projectId}/list_drone_ortho_dates/`;

    return this.http.get<TBResponse<OrthoDate[]>>(url, { params });
  }

  public updateAcBlockReviewedStatus(projectId: string, orthoId: string, data: UpdateAutoDetectionReviewedStatus[]) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${projectId}/drone_orthos/${orthoId}/pcs_ids`;

    return this.http.patch<TBResponse<OrthoAcBlock[]>>(url, data);
  }

  public addDronePano(projectId: string, data: FormData) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${projectId}/panos/`;

    return this.http.post<TBResponse<any>>(url, data).pipe(map((response) => response.data));
  }

  public getDronePanos(projectId: string, params: HttpParams) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${projectId}/panos/`;

    return this.http.get<PaginatedResponse<PanoramicImage>>(url, { params });
  }

  public getDronePano(projectId: string, panoId: string) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${projectId}/panos/${panoId}/`;

    return this.http.get<TBResponse<any>>(url).pipe(map((response) => response.data));
  }

  public deleteDronePano(projectId: string, panoId: string) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${projectId}/panos/${panoId}/`;

    return this.http.delete(url);
  }

  public updateDronePano(projectId: string, panoId: string, data: any) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${projectId}/panos/${panoId}/`;

    return this.http.patch<TBResponse<any>>(url, data).pipe(map((response) => response.data));
  }

  public getPanoListDates(projectId: string) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${projectId}/list_pano_dates/`;

    return this.http.get<TBResponse<any>>(url, { params: { page_size: 1000 } }).pipe(map((response) => response.data));
  }

  public patchSensors(constructionProjectId: string, sensors: Sensor[]) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${constructionProjectId}/sensors/`;

    return this.http.patch<TBResponse<Sensor[]>>(url, { sensors }).pipe(map((response) => response.data));
  }

  public addRover(constructionProjectId: string, data: RoverForm) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${constructionProjectId}/rovers/`;

    return this.http.post(url, data);
  }

  public deleteRover(constructionProjectId: string, rover_id: string) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${constructionProjectId}/rovers/${rover_id}/`;

    return this.http.delete(url);
  }

  public roverToggle(constructionProjectId: string, data: Rover) {
    const roverData = { ...data } as Partial<Rover>;
    delete roverData.uuid;
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${constructionProjectId}/rovers/${data.uuid}/`;

    return this.http.patch(url, roverData);
  }

  public addMilestone(projectId: string | undefined, milestone: AddMilestoneModel) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${projectId}/milestones/`;

    return this.http.post<TBResponse<IMilestone>>(url, milestone);
  }

  public editMilestone(projectId: string | undefined, milestone: AddMilestoneModel) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${projectId}/milestones/${milestone._id}/`;

    return this.http.patch<TBResponse<IMilestone>>(url, milestone);
  }

  public getMilestones(projectId: string, elementType?: string) {
    const params = new HttpParams({
      fromObject: {
        page_size: '1000',
        ...(elementType ? { element_type: elementType } : {}),
      },
    });
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${projectId}/milestones/`;

    return this.http.get<TBResponse<IMilestone[]>>(url, { params }).pipe(map((response) => response.data));
  }

  public deleteMilestone(projectId: string, milestoneId: string) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${projectId}/milestones/${milestoneId}/`;

    return this.http.delete(url);
  }

  public getSupplyPlanWorkWeeks(projectId: string) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${projectId}/supply_plan/`;

    return this.http.get<TBResponse<SupplyPlanResponseData>>(url);
  }

  public saveSupplyPlan(projectId: string, data: SupplyPlanResponseData) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${projectId}/supply_plan/`;

    return this.http.post<TBResponse<SupplyPlanResponseData>>(url, data);
  }

  public getEquipmentSpecs(projectId: string) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${projectId}/equipment_specs/`;

    return this.http.get<TBResponse<EquipmentSpecs>>(url).pipe(map((response) => response.data));
  }

  public saveEquipmentSpecs(projectId: string, data: EquipmentSpecs) {
    return this.http.post<EquipmentSpecs>(
      `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${projectId}/equipment_specs/`,
      data,
    );
  }

  public getEmptyPacksWorkWeeks(projectId: string) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${projectId}/empty_packs/`;

    return this.http.get<EmptyPackReturnsResponse>(url);
  }

  public saveEmptyPacks(projectId: string, data: EmptyPackReturnsResponseData) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${projectId}/empty_packs/`;

    return this.http.post<EmptyPackReturnsResponse>(url, data);
  }

  public getPCSClasses(projectId: string, droneOrthoId: string) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${projectId}/drone_orthos/${droneOrthoId}/all_class_counters_per_pcs/`;

    return this.http.get<TBResponse<DroneOrthoPcsClasses[]>>(url).pipe(map((response) => response.data));
  }

  public getPresetDates(projectId: string) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${projectId}/presets/`;

    return this.http.get<TBResponse<ProjectPreset>>(url).pipe(map((res) => res.data));
  }

  public saveWbsConfiguration(projectId: string, data: WbsConfigurationScope[]) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${projectId}/omega/wbs_config/`;

    return this.http.post<TBResponse<WbsConfiguration>>(url, { scopes: data });
  }

  public publishWbsConfiguration(projectId: string) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${projectId}/omega/wbs_config/publish/`;

    return this.http.put<TBResponse<WbsConfiguration>>(url, null);
  }
  public resetWbsConfiguration(projectId: string) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${projectId}/omega/wbs_config/reset/`;

    return this.http.patch<TBResponse<WbsConfiguration>>(url, null);
  }

  public updateWbsConfigurationPublished(projectId: string, data: WbsConfigEdit) {
    const copy: any = { ...data };
    copy.deleted_activities = copy.deleted_activities.map((uuid: string) => {
      return { activity_uuid: uuid };
    });
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${projectId}/omega/wbs_config/`;

    return this.http.put<TBResponse<WbsConfiguration>>(url, copy);
  }

  public getEod(date_from: string, date_to: string, scope: string, idProject: string) {
    const params = new HttpParams({ fromObject: { date_from, date_to, scope } });

    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${idProject}/omega/estimate_of_day/`;

    return this.http.get<TBResponse<EodByDate>>(url, { params }).pipe(map((response) => response.data));
  }

  public getEodCumulative(date: string, scope: string, idProject: string) {
    const params = new HttpParams({ fromObject: { date } });
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${idProject}/omega/estimate_of_day/${scope}/cumulative/`;

    return this.http
      .get<TBResponse<EstimateOfDayScopeCumulative>>(url, { params })
      .pipe(map((response) => response.data));
  }

  public saveEod(projectId: string, data: EstimateOfDayScope) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${projectId}/omega/estimate_of_day/`;

    return this.http.post<EstimateOfDayScope>(url, data);
  }

  public bulkSaveEods(projectId: string, data: BulkEodsToSave) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${projectId}/omega/estimate_of_day/bulk_create/`;

    return this.http.post<EstimateOfDayScope>(url, data);
  }

  public getEodsByUsers(constructionProjectId: string, selectedDate: string, activityId: string) {
    const params = new HttpParams({ fromObject: { date: selectedDate, activity_uuid: activityId } });
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${constructionProjectId}/omega/user_eods_by_group_element/`;

    return this.http.get<TBResponse<EodsByUserType[]>>(url, { params }).pipe(map((response) => response.data));
  }

  public lockScope(projectId: string, scope: string) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${projectId}/omega/scope_lock/${scope}/`;

    return this.http.post<TBResponse<ScopeLockInfo>>(url, undefined);
  }

  public cancelScope(projectId: string, scope: string) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${projectId}/omega/scope_lock/${scope}/`;

    return this.http.put<void>(url, undefined);
  }

  public getLockScope(projectId: string, scopeId: string) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${projectId}/omega/scope_lock/${scopeId}/`;

    return this.http.get<TBResponse<ScopeLockInfo>>(url, undefined).pipe(map((response) => response.data));
  }

  public getDailyReport(idProject: string, date: string, trendTimeFrame: number, forecastTimeFrame: number) {
    const params = new HttpParams({
      fromObject: {
        date,
        trend_period: trendTimeFrame,
        forecast_time_frame: forecastTimeFrame,
      },
    });

    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${idProject}/omega/daily_report/`;

    return this.http.get<TBResponse<DailyReportData>>(url, { params });
  }

  public getWbsConfig(idProject: string) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${idProject}/omega/wbs_config/`;

    return this.http.get<TBResponse<WbsConfiguration>>(url).pipe(map((response) => response.data));
  }
  public getWbsActivityUnits(projectId: string) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${projectId}/omega/wbs_config/units/`;

    return this.http.get<TBResponse<string[]>>(url).pipe(map((response) => response.data));
  }

  public verifyProjectInService(constructionProjectId: string) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${constructionProjectId}/verify_in_service/`;

    return this.http.post<void>(url, undefined);
  }

  public updateDailyDigest(projectId: string, data: ConstructionProjectSaveModel | undefined) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${projectId}/`;

    return this.http
      .patch<TBResponse<ConstructionProjectLoadModel>>(url, {
        ...data,
        trigger_generate_schedule: false,
      })
      .pipe(map((res) => res.data));
  }

  public generateProjectDataPackage(projectId: string) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${projectId}/package/`;

    return this.http.post<TBResponse<ConstructionProjectLoadModel>>(url, {}).pipe(map((res) => res.data));
  }

  public getContactProfileProjectUsers(projectId: string, contactProfiles: string[]) {
    const params = new HttpParams({
      fromObject: { contact_profile_ids: contactProfiles.join(',') },
    });

    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${projectId}/contact_profiles/`;

    return this.http.get<TBResponse<ProjectUserContactProfile[]>>(url, { params }).pipe(map((res) => res.data));
  }

  public getContactProfiles(
    constructionProjectId: string,
    usedProfiles: string[],
    searchQuery: string,
    page: number,
    pageSize = 10,
    isPilot = false,
    companyIds?: string[],
  ) {
    const params = new HttpParams({ fromObject: { page, page_size: pageSize } });
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${constructionProjectId}/all/companies/users/`;

    return this.http.post<PaginatedResponse<ContactProfileModel>>(
      url,
      {
        company_ids: companyIds,
        name: searchQuery || undefined,
        already_used_profiles: usedProfiles || undefined,
        disabled: false,
        is_pilot: isPilot,
      },
      { params },
    );
  }

  public getOriginalPlan(idProject: string, dateFrom: string, dateTo: string, idScope: string) {
    const params = new HttpParams({
      fromObject: {
        date_from: dateFrom,
        date_to: dateTo,
        scope: idScope,
      },
    });
    const url = `${environment.apiUrl}/${environment.apiVersion}/construction_projects/${idProject}/omega/plan/`;

    return this.http.get<TBResponse<WbsOriginalPlanData>>(url, { params }).pipe(map((response) => response.data));
  }

  public getEPSGData(spacialReferenceId: string) {
    return this.http
      .get<
        TBResponse<EPSGProjectionDataType>
      >(`${environment.apiUrl}/${environment.apiVersion}/epsg/${spacialReferenceId}`)
      .pipe(map((response) => response.data));
  }
}
